import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface FetchEngineFilters {
  featured?: boolean;
  status?: string;
  listingTypes?: string[];
  limit?: number;
  enabled?: boolean;
}

export const useFeaturedEngineListings = (filters: FetchEngineFilters = {}) => {
  const { enabled = true, ...queryFilters } = filters;
  
  return useQuery({
    queryKey: ['featured-engines', queryFilters],
    enabled,
    queryFn: async () => {
      let query = supabase
        .from('engine_listings')
        .select('*, engine_modules(*)')
        .order('created_at', { ascending: false });

      if (filters.featured !== undefined) {
        query = query.eq('featured', filters.featured);
      }

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.listingTypes && filters.listingTypes.length > 0) {
        query = query.in('listing_type', filters.listingTypes);
      }

      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      if (!data || data.length === 0) return [];
      
      // BATCH: Fetch all media in a single query instead of N queries
      const listingIds = data.map(l => l.id);
      
      const { data: allMedia } = await supabase
        .from('listing_media')
        .select('listing_id, file_url, metadata, created_at, display_order')
        .in('listing_id', listingIds)
        .eq('media_type', 'photo')
        .order('display_order', { ascending: true });

      // Process media for each listing client-side
      const listingsWithHero = data.map((listing) => {
        const media = (allMedia || []).filter(m => m.listing_id === listing.id);
        const urls = [...new Set(media.map(m => m.file_url).filter(Boolean))];

        // Find hero image: pick newest with category === 'hero', else first photo
        const heroMediaItems = media.filter(m => (m.metadata as any)?.category === 'hero');
        const bestHero = heroMediaItems.length > 0
          ? heroMediaItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
          : null;

        const heroImage = bestHero?.file_url || urls[0] || null;
        const photos = heroImage ? [heroImage, ...urls.filter(u => u !== heroImage)] : urls;

        return {
          ...listing,
          heroImage,
          photos,
        };
      });
      
      return listingsWithHero;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
